/* -------------------------------------------------------------------------- */
/*                                  Packages                                  */
/* -------------------------------------------------------------------------- */
import * as React from "react"
import { Controller, useWatch } from "react-hook-form"
import Cleave from "cleave.js/react"
import { Tooltip } from "react-tooltip"
import { twMerge } from "tw-merge"
import RangeSlider from "react-range-slider-input"
import CreateableSelect from "react-select/creatable"
import { ArrowLeft, Check } from "react-feather"
import { isValidPhoneNumber } from "libphonenumber-js"

/* -------------------------------------------------------------------------- */
/*                               External Utils                               */
/* -------------------------------------------------------------------------- */
import { currencyFormatter } from "@utils/utils"
import ReactSelect from "react-select"

/* -------------------------------------------------------------------------- */
/*                               External Types                               */
/* -------------------------------------------------------------------------- */
import { ElementProps } from "@components/prequalify/Funnel"

/* -------------------------------------------------------------------------- */
/*                                  Functions                                 */
/* -------------------------------------------------------------------------- */
export const formatFormValues = ({ formValues }) => {
  let newFormValues = {}
  Object.keys(formValues).map(key => {
    const fieldValue = formValues[key]
    if (
      fieldValue &&
      typeof fieldValue === "object" &&
      !Array.isArray(fieldValue)
    ) {
      newFormValues[key] = fieldValue.value
    } else {
      newFormValues[key] = fieldValue
    }
  })
  return newFormValues
}
const checkConditions = ({ formValues, conditions, resetField }) => {
  const checkConditionMatch = ({
    conditionValue,
    matchCondition,
    fieldValue,
    operator,
    resetFieldsObject,
  }) => {
    let conditionIsMatched
    if (operator === "none") {
      conditionIsMatched =
        matchCondition === "eq"
          ? conditionValue !== fieldValue
          : !conditionValue.match(fieldValue)
    } else {
      conditionIsMatched =
        matchCondition === "eq"
          ? conditionValue === fieldValue
          : conditionValue.match(fieldValue)
    }

    return conditionIsMatched
  }

  return conditions
    .filter(condition => condition)
    .every(condition => {
      const { values, fieldName, matchCondition, operator, resetFieldsObject } =
        condition
      const fieldValue = formValues[fieldName]
      let conditionIsMatched
      if (values.length === 1) {
        conditionIsMatched = checkConditionMatch({
          conditionValue: values[0],
          matchCondition,
          fieldValue,
          operator,
          resetFieldsObject,
        })
      } else {
        if (condition.operator === "and" || condition.operator === "none") {
          conditionIsMatched = Array.isArray(fieldValue)
            ? fieldValue.every(currentFieldValue => {
                return values.find(conditionValue =>
                  checkConditionMatch({
                    conditionValue,
                    matchCondition,
                    fieldValue: currentFieldValue,
                    operator,
                    resetFieldsObject,
                  }),
                )
              })
            : values.every(currentValue =>
                checkConditionMatch({
                  conditionValue: currentValue,
                  matchCondition,
                  fieldValue,
                  operator,
                  resetFieldsObject,
                }),
              )
        } else if (condition.operator === "or") {
          conditionIsMatched = Array.isArray(fieldValue)
            ? fieldValue.some(currentFieldValue => {
                return values.find(conditionValue =>
                  checkConditionMatch({
                    conditionValue,
                    matchCondition,
                    fieldValue: currentFieldValue,
                    operator,
                    resetFieldsObject,
                  }),
                )
              })
            : values.find(currentValue =>
                checkConditionMatch({
                  conditionValue: currentValue,
                  matchCondition,
                  fieldValue,
                  operator,
                  resetFieldsObject,
                }),
              )
        }
      }
      if (!conditionIsMatched && resetFieldsObject) {
        for (const fieldToReset in resetFieldsObject) {
          const resetValue = resetFieldsObject[fieldToReset]
          if (formValues[fieldToReset] !== resetValue) {
            resetField(fieldToReset, {
              defaultValue: resetValue,
            })
          }
        }
      }
      return conditionIsMatched
    })
}

/* -------------------------------------------------------------------------- */
/*                                 Validation                                 */
/* -------------------------------------------------------------------------- */
export const requiredRuleWithoutMessage = {
  required: {
    value: true,
  },
}
export const requiredRule = {
  required: {
    value: true,
    message: `Please fill out this field.`,
  },
}
export const requiredOptionRule = {
  required: {
    value: true,
    message: `Please select one of the options.`,
  },
}

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
export interface FormElementClasses {
  default?: string
  disabled?: string
  error?: string
  focused?: string
  selected?: string
}
type InputTypes = "text" | "email" | "phone" | "range" | "radio" | "checkbox"
type FormElementTypes = "input" | "select" | "datalist"
interface ValidationRules {
  required: {
    value: string
    message?: string
  }
  pattern: {
    value: string
    message?: string
  }
}
type Conditions = {
  fieldName: string
  matchCondition: "eq" | "match"
  operator: "and" | "or" | "none"
  values: Array<string>
}
export interface FormField {
  controllerComponent: {
    name: string
    props: object
  }
  disabled?: boolean
  formElement: FormElement
  heading?: ElementProps
  label?: ElementProps
  useResetButton?: boolean
}

interface SelectFieldOption {
  label: string
  value: string
}
interface FormElement extends ElementProps {
  attributes: {
    max?: number
    min?: number
    name: string
    id?: string
    type?: string
  }
  defaultValue?: string
  conditions?: Array<Conditions>
  options: Array<SelectFieldOption>
  optionsByFieldValue?: Array<object>
  type: FormElementTypes
  validationRules?: ValidationRules
}
export interface InputGroup {
  conditions: Conditions
  fields: Array<FormElement>
  name: string
  type: InputTypes
  validationRules?: ValidationRules
}
interface Form {
  clearErrors: Function
  controller: {
    control?: Function
  }
  getValues?: Function
  errors?: object
  register: Function
  reset: Function
  resetField?: Function
  setValue?: Function
}
interface TooltipAttributes {
  placement?: string
}
interface TooltipProps {
  className?: string
  id: string
  isOpen: boolean
}
interface InputField {
  field: FormField & {
    inputGroup?: InputGroup
  }
  form: Form
  tooltipAttributes: TooltipAttributes
  tooltipProps?: TooltipProps
}

const FormTooltip = props => {
  return (
    <Tooltip
      isOpen={props.isOpen}
      id={props.id}
      className={`!bg-sunset-orange max-w-full !text-[13px] z-[20] ${
        props.classname ? props.className : ""
      }`}
      opacity={1}
      {...props.tooltipProps}
    />
  )
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */
let inputFieldClasses = {
  formElement: {
    checkbox: {
      default: `[--size:10px] appearance-none border box-content grid p-[1px] items-center justify-items-center border-grey-6 h-[var(--size)] rounded-[2px] w-[var(--size)] relative checked:border-pacific-blue checked:before:absolute checked:before:bg-pacific-blue checked:before:h-[calc(var(--size)-2px)] checked:before:rounded-[2px] checked:before:w-[calc(var(--size)-2px)] disabled:checked:before:bg-grey-4 disabled:border-grey-4`,
      error: `border-sunset-orange-6`,
    },
    radio: {
      default: `appearance-none border border-grey-8 box-content grid p-[1px] items-center justify-items-center h-[12px] relative rounded-full w-[12px]  checked:border-pacific-blue checked:before:absolute checked:before:bg-pacific-blue checked:before:h-[10px] checked:before:rounded-full checked:before:w-[10px] disabled:border-grey-4 disabled:checked:before:bg-grey-4 disabled:border-grey-4`,
      error: `border-sunset-orange-6`,
    },
    range: {
      default: `[--thumbColor:#278DE5] [--thumbSize:26px] !h-[6px] w-full ${"[&]:!bg-[rgba(0,60,72,0.16)] [&_div]:!bg-[var(--thumbColor)]"} [&_[class*=thumb]]:border-[6px] [&_[class*=thumb]]:border-white [&_[class*=thumb]]:!h-[var(--thumbSize)] [&_[class*=thumb]]:outline [&_[class*=thumb]]:outline-[1px] [&_[class*=thumb]]:outline-[rgba(0,60,72,0.16)] [&_[class*=thumb]]:shadow-md [&_[class*=thumb]]:!w-[var(--thumbSize)] [&[data-disabled]_div]:!bg-grey-4`,
      error: ``,
    },
    text: {
      default: `caption font-variant-numeric--secondary border-b pb-[2px] [outline:0] rounded-none text-grey-8 focus:border-b-pacific-blue disabled:border-grey-4 disabled:text-grey-4 disabled:bg-transparent`,
      error: `border-sunset-orange-6`,
    },
  },
  formElementAllContainer: {
    text: {
      default: `flex flex-col gap-[10px]`,
      error: ``,
    },
  },
  formElementGroup: {
    checkbox: {
      default: `flex gap-[8px] items-center`,
      error: ``,
    },
    radio: {
      default: `flex gap-[5px] items-center`,
      error: ``,
    },
    range: {
      default: `max-w-[485px]`,
      error: ``,
    },
    text: {
      default: `flex flex-col gap-[5px]`,
      error: ``,
    },
  },
  formElementGroupsContainer: {
    checkbox: {
      default: `flex gap-[16px]`,
      error: ``,
    },
    radio: {
      default: `flex gap-[16px]`,
      error: ``,
    },
    text: {
      default: `flex flex-col gap-[5px]`,
      error: ``,
    },
  },
  heading: {
    text: {
      default: `font-variant-numeric--secondary leading-[16px] text-[13px] text-grey-6`,
      error: ``,
    },
  },
  label: {
    radio: {
      default: `caption font-variant-numeric--secondary text-grey-8 `,
      error: ``,
    },
    range: {
      default: `leading-[20px] text-[13px] text-grey-6`,
      error: ``,
    },
    text: {
      default: `font-variant-numeric--secondary leading-[normal] text-[13px] text-grey-6`,
      error: ``,
    },
  },
  labelContainer: {
    range: {
      default: `flex items-center justify-between mb-[16px]`,
      error: ``,
    },
  },
  minMaxLabel: {
    range: {
      default: `font-bold leading-[120%] text-[8px] text-grey-6 tracking-[0.08px] leading-[20px] `,
      error: ``,
    },
  },
  minMaxLabelContainer: {
    range: {
      default: `flex justify-between [margin-block:24px_19px]`,
      error: ``,
    },
  },
  priceRange: {
    range: {
      default: `flex gap-[5px] items-start`,
      error: ``,
    },
  },
  undecidedFormElement: {
    range: {
      default: `[--size:10px] appearance-none border box-content grid p-[1px] items-center justify-items-center border-grey-6 h-[var(--size)] rounded-[2px] w-[var(--size)] relative checked:border-pacific-blue checked:before:absolute checked:before:bg-pacific-blue checked:before:h-[calc(var(--size)-2px)] checked:before:rounded-[2px] checked:before:w-[calc(var(--size)-2px)] disabled:checked:before:bg-grey-4 disabled:border-grey-4`,
      error: ``,
    },
  },
  undecidedFormElementGroup: {
    range: {
      default: `flex gap-[8px]`,
      error: ``,
    },
  },
  undecidedLabel: {
    range: {
      default: `font-variant-numeric--primary leading-[15px] text-[15px] text-grey-8`,
      error: ``,
    },
  },
}

const otherInputTypes = ["checkbox", "radio"]
for (const key in inputFieldClasses) {
  otherInputTypes.forEach(value => {
    if (!inputFieldClasses[key][value]) {
      inputFieldClasses[key][value] = inputFieldClasses[key].text
    }
  })
}

const selectFieldTextClass = `font-variant-numeric--primary leading-[22.8px] text-[16px] text-grey-8 `
const disabledSelectFieldTextClass = `text-grey-4`
const selectFieldClasses = {
  formElement: {
    container: {
      default: `z-[100]`,
      focused: `z-[110]`,
    },
    control: {
      default: `!border-t-0 !border-r-0 !border-l-0 border-b !border-b-grey-6 [outline:0] !rounded-none`,
      disabled: `!bg-transparent border-grey-4`,
      error: `!border-b-sunset-orange-6 `,
      focused: `!shadow-none`,
      selected: ``,
    },
    dropdownIndicator: {
      default: `[&_svg]:fill-grey-6`,
      disabled: `[&_svg]:fill-grey-4`,
      error: `[&_svg]:fill-sunset-orange-6`,
      focused: ``,
      selected: ``,
    },
    indicatorSeparator: {
      default: `hidden`,
      disabled: ``,
      error: ``,
      focused: ``,
      selected: ``,
    },
    input: {
      default: selectFieldTextClass,
      disabled: ``,
      error: ``,
      focused: ``,
      selected: ``,
    },
    menu: {
      default: `bg-white`,
      disabled: ``,
      error: ``,
      focused: ``,
      selected: ``,
    },
    menuList: {
      default: `bg-white ${selectFieldTextClass}`,
      disabled: ``,
      error: ``,
      focused: ``,
      selected: ``,
    },
    option: {
      default: `bg-white ${selectFieldTextClass} hover:bg-pacific-blue-1 hover:text-grey-8`,
      disabled: ``,
      error: ``,
      focused: `bg-pacific-blue text-white`,
      selected: `bg-pacific-blue text-white`,
    },
    placeholder: {
      default: selectFieldTextClass,
      disabled: disabledSelectFieldTextClass,
      error: ``,
      focused: ``,
      selected: ``,
    },
    singleValue: {
      default: selectFieldTextClass,
      disabled: disabledSelectFieldTextClass,
      error: ``,
      focused: ``,
      selected: ``,
    },
    valueContainer: {
      default: `![padding:0]`,
      disabled: ``,
      error: ``,
      focused: ``,
      selected: ``,
    },
  },
  formElementGroup: {
    default: `flex flex-col gap-[5px] max-w-[485px]`,
    error: ``,
  },
  label: {
    default: inputFieldClasses.label.text.default,
    error: ``,
  },
  helpElement: {
    default: `${inputFieldClasses.label.radio.default} mt-[10px]`,
    error: ``,
  },
}

/* -------------------------------------------------------------------------- */
/*                                 Components                                 */
/* -------------------------------------------------------------------------- */
const ResetButton = props => {
  const classes = props.field.resetButton?.classes

  let resetFieldsObject = props.field.resetButton.resetFieldsObject || {}

  return (
    <button
      className={twMerge(
        `flex gap-[10px] items-center mb-[10px] font-['Inter'] leading-[20px] text-[15px] text-[#0C1112] ${
          classes?.default ? classes?.default : ""
        } ${props.error ? classes?.error || "" : ""}`,
      )}
      onClick={() => {
        for (const fieldToReset in resetFieldsObject) {
          const resetValue = resetFieldsObject[fieldToReset]
          props.form.resetField(fieldToReset, {
            defaultValue: resetValue,
          })
        }
      }}
      type="button"
    >
      {props.field.resetIcon || (
        <ArrowLeft width={20} height={20} color={`#0C1112`} />
      )}
      {props.field.reset?.text || `Back`}
    </button>
  )
}

const InputElement = props => {
  const formElement = props.field.formElement
  const inputGroup = props.field.inputGroup
  const formElementAttributes = formElement?.attributes
  const name = formElementAttributes?.name || inputGroup?.name
  const id = formElementAttributes?.id
  const type = formElementAttributes?.type || inputGroup?.type

  return (
    <input
      id={id || name}
      {...props.form.register(name, {
        ...(formElement?.validationRules || inputGroup?.validationRules),
      })}
      className={props.className}
      type={type}
      {...props.tooltipAttributes}
      {...formElementAttributes}
      disabled={props.field.disabled}
      value={formElementAttributes?.value}
      onKeyUp={e => {
        if (formElementAttributes?.onKeyUp) formElementAttributes?.onKeyUp()
        if (props.tabToNextField) props.tabToNextField(e, name)
      }}
    />
  )
}

const LabelElement = props => {
  const formElement = props.field.formElement
  const inputGroup = props.field.inputGroup
  const formElementAttributes = formElement?.attributes
  const name = formElementAttributes?.name || inputGroup?.name
  const id = formElementAttributes?.id

  return (
    <label className={props.className} htmlFor={id || name}>
      {props.field.label?.text}
    </label>
  )
}

export const InputField = (props: InputField) => {
  let formElement = props.field.formElement
  let formElementAttributes = formElement?.attributes
  let formElementType = formElementAttributes?.type
  let label = props.field.label
  let inputGroup = props.field.inputGroup
  let errors = props.form.errors
  let formElementName = formElementAttributes?.name || inputGroup?.name
  let error = errors[formElementName]
  let validationRules =
    formElement?.validationRules || inputGroup?.validationRules

  const getClass = ({ error, classes, inputFieldClasses }) =>
    twMerge(
      `${inputFieldClasses?.default} ${
        classes?.default ? classes?.default : ""
      } ${
        error
          ? twMerge(
              `${inputFieldClasses?.error} ${
                classes?.error ? classes.error : ""
              }`,
            )
          : ""
      }`,
    )

  const classInputType = inputGroup
    ? inputGroup.type
    : formElementType === "email" || formElementType === "phone"
      ? "text"
      : formElementType

  let labelClass = getClass({
    error,
    classes: label?.classes,
    inputFieldClasses: inputFieldClasses.label[classInputType],
  })
  let formElementClass = getClass({
    error,
    classes: formElement?.classes,
    inputFieldClasses: inputFieldClasses.formElement[classInputType],
  })

  let formElementGroupClass = getClass({
    error,
    classes: props.field.formElementGroup?.classes,
    inputFieldClasses: inputFieldClasses.formElementGroup[classInputType],
  })
  let formElementGroupsContainerClass = getClass({
    error,
    classes: props.field.formElementGroupsContainer?.classes,
    inputFieldClasses:
      inputFieldClasses.formElementGroupsContainer[classInputType],
  })
  let formElementAllContainerClass = getClass({
    error,
    classes: props.field.formElementAllContainer?.classes,
    inputFieldClasses:
      inputFieldClasses.formElementAllContainer[classInputType],
  })

  let headingClass = getClass({
    error,
    classes: props.field.heading?.classes,
    inputFieldClasses: inputFieldClasses.heading[classInputType],
  })

  const getTooltipAttributes = ({ id, error, tooltipPlacement }) => {
    return {
      "data-tooltip-id": id,
      "data-tooltip-content": error?.message
        ? error.message
        : error && validationRules && validationRules[error.type]?.message,
      "data-tooltip-place": tooltipPlacement || "bottom-end",
    }
  }

  const getFormTooltipProps = ({ error, id, tooltipProps }) => {
    return {
      isOpen: error?.message ? true : false,
      id,
      tooltipProps,
    }
  }

  let tooltipAttributes = getTooltipAttributes({
    id:
      formElementAttributes?.id ||
      formElementAttributes?.name ||
      inputGroup?.name,
    error,
    tooltipPlacement: props.field.tooltipAttributes?.placement,
  })

  let inputElementProps = {
    ...props,
    className: formElementClass,
    tooltipAttributes,
  }

  let formTooltipProps = getFormTooltipProps({
    error,
    id:
      formElementAttributes?.id ||
      formElementAttributes?.name ||
      inputGroup?.name,
    tooltipProps: props.tooltipProps,
  })

  const formValues = useWatch({
    control: props.form.controller.control,
  })

  const formattedFormValues = formatFormValues({ formValues })

  let showField =
    formElement?.conditions || inputGroup?.conditions
      ? checkConditions({
          formValues: formattedFormValues,
          conditions: formElement?.conditions || inputGroup?.conditions,
          resetField: props.form.resetField,
        })
        ? true
        : false
      : true

  React.useEffect(() => {
    // Range values
    if (!formValues[formElementAttributes?.name] && formElement?.defaultValue) {
      props.form.setValue(
        formElementAttributes?.name,
        formElement?.defaultValue,
      )
    }
  }, [formValues])

  React.useEffect(() => {
    if (!showField && error) {
      props.form.clearErrors(formElementName)
    }
  }, [showField, error])

  const tabToNextField = React.useCallback(
    (e, formElementName) => {
      if (!props.useTabToNextField) return
      if (e.key === "Enter") {
        let nextFieldNameIndex
        for (let i = 0; i < props.groupedFieldNames.length; i++) {
          let currentFieldName = props.groupedFieldNames[i]
          if (currentFieldName === formElementName) {
            nextFieldNameIndex =
              i + 1 < props.groupedFieldNames.length ? i + 1 : -1
          }
        }
        if (nextFieldNameIndex > -1) {
          props.form.setFocus(props.groupedFieldNames[nextFieldNameIndex])
        }
      }
    },
    [props.groupedFieldNames, props.form.setFocus, props.useTabToNextField],
  )

  let FormElementGroupTag = props.field.formElementGroup?.tagName || "div"

  if (inputGroup) {
    let heading = props.field.heading
    return (
      showField && (
        <div className={formElementAllContainerClass}>
          {heading && <div className={headingClass}>{heading.text}</div>}
          {/* Option Fields Container */}
          <div
            className={formElementGroupsContainerClass}
            {...tooltipAttributes}
          >
            <FormTooltip {...formTooltipProps} />
            {inputGroup.fields.map((option, i) => {
              let { formElement } = option
              label = option.label
              labelClass = getClass({
                error,
                classes: label?.classes,
                inputFieldClasses: inputFieldClasses.label[classInputType],
              })

              formElementClass = getClass({
                error,
                classes: formElement?.classes,
                inputFieldClasses:
                  inputFieldClasses.formElement[classInputType],
              })

              let newInputElementProps = { ...inputElementProps }
              delete newInputElementProps["tooltipAttributes"]
              return (
                <FormElementGroupTag
                  key={formElement.attributes.id}
                  className={formElementGroupClass}
                  {...props.field.formElementGroup?.attributes}
                >
                  <InputElement
                    {...newInputElementProps}
                    className={formElementClass}
                    field={{
                      ...props.field,
                      ...option,
                      formElement,
                      inputGroup: {
                        ...props.field.inputGroup,
                        // Only add validation rules to 1st radio field
                        validationRules: i === 0 ? validationRules : {},
                      },
                    }}
                  />
                  {label && (
                    <LabelElement
                      {...props}
                      className={labelClass}
                      field={{
                        ...props.field,
                        ...option,
                        formElement,
                      }}
                    />
                  )}
                </FormElementGroupTag>
              )
            })}
          </div>
        </div>
      )
    )
  } else if (formElementType === "checkbox" || formElementType === "radio") {
    return (
      showField && (
        <div className={formElementGroupClass}>
          <FormTooltip {...formTooltipProps} />
          <InputElement {...inputElementProps} />
          {label && <LabelElement {...props} className={labelClass} />}
        </div>
      )
    )
  } else if (formElementType === "range") {
    let labelContainerClass = getClass({
      error,
      classes: props.field.labelContainer?.classes,
      inputFieldClasses: inputFieldClasses.labelContainer[classInputType],
    })
    let priceRangeClass = getClass({
      error,
      classes: props.field.priceRange?.classes,
      inputFieldClasses: inputFieldClasses.priceRange[classInputType],
    })
    let minMaxLabelClass = getClass({
      error,
      classes: props.field.minMaxLabel?.classes,
      inputFieldClasses: inputFieldClasses.minMaxLabel[classInputType],
    })
    let minMaxLabelContainerClass = getClass({
      error,
      classes: props.field.minMaxLabelContainer?.classes,
      inputFieldClasses: inputFieldClasses.minMaxLabelContainer[classInputType],
    })
    let undecidedFormElementGroupClass = getClass({
      error,
      classes: props.field.undecidedFormElementGroup?.classes,
      inputFieldClasses:
        inputFieldClasses.undecidedFormElementGroup[classInputType],
    })
    let undecidedFormElementClass = getClass({
      error,
      classes: props.field.undecidedFormElement?.classes,
      inputFieldClasses: inputFieldClasses.undecidedFormElement[classInputType],
    })
    let undecidedLabelClass = getClass({
      error,
      classes: props.field.undecidedLabel?.classes,
      inputFieldClasses: inputFieldClasses.undecidedLabel[classInputType],
    })

    return (
      showField && (
        <div className={formElementGroupClass}>
          <Controller
            control={props.form.controller.control}
            name={formElementAttributes?.name}
            render={({ field: { ref, ...formField } }) => {
              const fieldProps = {
                ...props.field.formElement,
                ...formField,
              }

              return (
                <>
                  <div className={labelContainerClass}>
                    <div className={labelClass}>{label.text}</div>
                    {/* Min/Max */}
                    <div className={priceRangeClass}>
                      {formField.value === "undecided" ? (
                        <div className={labelClass}>N/A</div>
                      ) : (
                        <>
                          <div className={labelClass}>
                            {currencyFormatter.format(
                              (formField.value || formElement?.defaultValue)[0],
                            )}
                          </div>
                          <div className={labelClass}>-</div>
                          <div className={labelClass}>
                            {currencyFormatter.format(
                              (formField.value || formElement?.defaultValue)[1],
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <RangeSlider
                    {...fieldProps}
                    ref={ref}
                    max={formElementAttributes?.max}
                    min={formElementAttributes?.min}
                    step={formElementAttributes?.step}
                    className={formElementClass}
                    defaultValue={
                      formField.value !== "undecided"
                        ? formField.value || formElement?.defaultValue
                        : undefined
                    }
                    onInput={value => {
                      formField.onChange(value)
                    }}
                  />
                  {/* Min/Max Label Container */}
                  {/* <div className={minMaxLabelContainerClass}>
                    <div className={minMaxLabelClass}>
                      {currencyFormatter.format(formElementAttributes?.min)}
                    </div>
                    <div className={minMaxLabelClass}>
                      {currencyFormatter.format(formElementAttributes?.max)}
                    </div>
                  </div> */}
                  <div
                    className={`[margin-block-start:calc(19px+(26px/2))] ${undecidedFormElementGroupClass}`}
                  >
                    <input
                      className={undecidedFormElementClass}
                      id={`${formElementAttributes?.name}-unsure`}
                      type="checkbox"
                      onChange={e => {
                        props.form.setValue(
                          formElementAttributes?.name,
                          e.target.checked
                            ? "undecided"
                            : formElement?.defaultValue,
                        )
                      }}
                      disabled={props.field.disabled}
                      checked={formField.value === "undecided"}
                    />
                    <label className={undecidedLabelClass} htmlFor="unsure">
                      {props.field.undecidedLabel?.text || `Unsure`}
                    </label>
                  </div>
                </>
              )
            }}
          />
        </div>
      )
    )
  } else if (
    props.field.controllerComponent?.name === "cleave" ||
    formElementType === "email" ||
    formElementType === "phone"
  ) {
    return (
      showField && (
        <div className={formElementGroupClass}>
          <FormTooltip {...formTooltipProps} />
          {label && <LabelElement {...props} className={labelClass} />}
          <Controller
            control={props.form.controller.control}
            rules={{
              ...(formElementType === "email"
                ? {
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message:
                        validationRules?.pattern?.message ||
                        `Please enter a valid email address (e.g. example@example.com)`,
                    },
                    ...validationRules,
                  }
                : formElementType === "phone"
                  ? {
                      validate: value => {
                        return isValidPhoneNumber(value, "US")
                          ? true
                          : `Please enter a valid phone number (e.g 123-456-7891)`
                      },
                      // pattern: {
                      //   value: /\d{3}-\d{3}-\d{4}$/,
                      //   message:
                      //     validationRules?.pattern?.message ||
                      //     `Please enter a valid phone number (e.g 123-456-7891)`,
                      // },
                      ...validationRules,
                    }
                  : validationRules),
            }}
            name={formElementAttributes?.name}
            render={({ field: { ref, ...formField } }) => {
              let cleaveOptions =
                props.field.controllerComponent?.props?.options
              return (
                <Cleave
                  options={
                    formElementType === "phone"
                      ? {
                          phone: true,
                          phoneRegionCode: "us",
                          delimiter: "-",
                          ...cleaveOptions,
                        }
                      : {
                          ...cleaveOptions,
                        }
                  }
                  id={formElementAttributes?.id || formElementAttributes?.name}
                  disabled={props.field.disabled}
                  className={formElementClass}
                  {...tooltipAttributes}
                  htmlRef={ref}
                  {...formField}
                  onKeyUp={e => tabToNextField(e, formElementName)}
                />
              )
            }}
          />
        </div>
      )
    )
  }
  return (
    showField && (
      <div className={formElementGroupClass}>
        <FormTooltip {...formTooltipProps} />
        {label && <LabelElement {...props} className={labelClass} />}
        <InputElement {...inputElementProps} tabToNextField={tabToNextField} />
      </div>
    )
  )
}

export const SelectField = (props: InputField) => {
  let formElement = props.field.formElement
  let formElementAttributes = formElement?.attributes
  let formElementName = formElementAttributes?.name
  let label = props.field.label
  let errors = props.form.errors
  let error = errors[formElementName]
  const getClass = ({ error, classes, selectFieldClasses }) =>
    twMerge(
      `${selectFieldClasses?.default} ${
        classes?.default ? classes?.default : ""
      } ${
        error
          ? twMerge(
              `${selectFieldClasses?.error} ${
                classes?.error ? classes.error : ""
              }`,
            )
          : ""
      }`,
    )
  const getReactSelectClassNames = ({ error, classes, selectFieldClasses }) => {
    let reactSelectClassNames = {}
    Object.keys(selectFieldClasses).forEach(key => {
      const currentSelectFieldClass = selectFieldClasses[key]
      const currentClass = classes ? classes[key] : {}
      reactSelectClassNames[key] = state =>
        twMerge(
          ` ${currentSelectFieldClass?.default} ${
            currentClass?.default ? currentClass?.default : ""
          }
          ${
            state.isSelected
              ? twMerge(
                  `${currentSelectFieldClass?.selected} ${
                    currentClass?.selected ? currentClass.selected : ""
                  }`,
                )
              : ""
          }
           ${
             state.isFocused
               ? twMerge(
                   `${currentSelectFieldClass?.focused} ${
                     currentClass?.focused ? currentClass.focused : ""
                   }`,
                 )
               : ""
           } ${
             error
               ? twMerge(
                   `${currentSelectFieldClass?.error} ${
                     currentClass?.error ? currentClass.error : ""
                   }`,
                 )
               : ""
           } ${
             state.isDisabled
               ? twMerge(
                   `${currentSelectFieldClass?.disabled} ${
                     currentClass?.disabled ? currentClass.disabled : ""
                   }`,
                 )
               : ""
           }`,
        )
    })

    return reactSelectClassNames
  }
  let labelClass = getClass({
    error,
    classes: label?.classes,
    selectFieldClasses: selectFieldClasses.label,
  })
  let formElementClass = getReactSelectClassNames({
    error,
    classes: props.field.formElement?.classes,
    selectFieldClasses: selectFieldClasses.formElement,
  })
  let formElementGroupClass = getClass({
    error,
    classes: props.field.formElementGroup?.classes,
    selectFieldClasses: selectFieldClasses.formElementGroup,
  })

  const getTooltipAttributes = ({ id, error, tooltipPlacement }) => {
    return {
      "data-tooltip-id": id,
      "data-tooltip-content": error?.message,
      "data-tooltip-place": tooltipPlacement || "bottom-end",
    }
  }

  const getFormTooltipProps = ({ error, id, tooltipProps }) => {
    return {
      isOpen: error?.message ? true : false,
      id,
      tooltipProps,
    }
  }

  let tooltipAttributes = getTooltipAttributes({
    id: formElementAttributes?.id || formElementAttributes?.name,
    error,
    tooltipPlacement: props.field.tooltipAttributes?.placement,
  })

  let formTooltipProps = getFormTooltipProps({
    error,
    id: formElementAttributes?.id || formElementAttributes?.name,
    tooltipProps: props.tooltipProps,
  })

  const formValues = useWatch({
    control: props.form.controller.control,
  })

  const formattedFormValues = formatFormValues({ formValues })

  const conditions = props.field.formElement.conditions

  const showField = conditions
    ? checkConditions({
        formValues: formattedFormValues,
        conditions,
        resetField: props.form.resetField,
      })
      ? true
      : false
    : true

  React.useEffect(() => {
    if (!showField && error) {
      props.form.clearErrors(formElementName)
    }
  }, [showField, error])

  const options = formElement?.optionsByFieldValue
    ? formElement?.optionsByFieldValue[
        formattedFormValues[formElement?.optionFieldName]
      ]
    : formElement?.options

  React.useEffect(() => {
    // Set value to null if current value is not one of select options
    const formElementValue =
      formValues[formElementName]?.value || formValues[formElementName]
    if (
      formValues[formElementName] &&
      !options?.some(option => {
        const optionValue = option.value || option
        return formElementValue === optionValue
      })
    ) {
      props.form.resetField(formElementName, {
        defaultValue: null,
      })
    }
  }, [formValues])

  return (
    showField && (
      <div className={formElementGroupClass} {...tooltipAttributes}>
        <FormTooltip {...formTooltipProps} />
        {label && <LabelElement {...props} className={labelClass} />}
        <Controller
          control={props.form.controller.control}
          name={formElementAttributes?.name}
          rules={{ ...formElement?.validationRules }}
          render={({ field: { ref, ...formField } }) => {
            return (
              <>
                {props.field.resetButton && <ResetButton {...props} />}
                <ReactSelect
                  options={options.map(option => {
                    if (!option.label || !option.value) {
                      return {
                        label: option.label || option,
                        value: option.value || option,
                      }
                    }
                    return option
                  })}
                  {...formField}
                  {...formElementAttributes}
                  onChange={e => {
                    if (props.field.formElement.attributes?.onChange) {
                      props.field.formElement.attributes?.onChange()
                    }
                    formField.onChange(e)
                  }}
                  value={
                    // If value is a string (previous field used was not a select field)
                    formField.value && typeof formField.value !== "object"
                      ? options?.find(option => {
                          return option.value === formField.value
                        }) || null
                      : formField.value || null
                  }
                  isDisabled={props.field.disabled}
                  classNames={formElementClass}
                  ref={ref}
                />
              </>
            )
          }}
        />
      </div>
    )
  )
}

export const DataListField = (props: InputField) => {
  let formElement = props.field.formElement
  let formElementAttributes = formElement?.attributes
  let label = props.field.label
  let errors = props.form.errors
  let formElementName = formElementAttributes?.name
  let error = errors[formElementName]
  const getClass = ({ error, classes, selectFieldClasses }) =>
    twMerge(
      `${selectFieldClasses?.default} ${
        classes?.default ? classes?.default : ""
      } ${
        error
          ? twMerge(
              `${selectFieldClasses?.error} ${
                classes?.error ? classes.error : ""
              }`,
            )
          : ""
      }`,
    )
  const getReactSelectClassNames = ({ error, classes, selectFieldClasses }) => {
    let reactSelectClassNames = {}
    Object.keys(selectFieldClasses).forEach(key => {
      const currentSelectFieldClass = selectFieldClasses[key]
      const currentClass = classes ? classes[key] : {}
      reactSelectClassNames[key] = state =>
        twMerge(
          ` ${currentSelectFieldClass?.default} ${
            currentClass?.default ? currentClass?.default : ""
          }
          ${
            state.isSelected
              ? twMerge(
                  `${currentSelectFieldClass?.selected} ${
                    currentClass?.selected ? currentClass.selected : ""
                  }`,
                )
              : ""
          }
           ${
             state.isFocused
               ? twMerge(
                   `${currentSelectFieldClass?.focused} ${
                     currentClass?.focused ? currentClass.focused : ""
                   }`,
                 )
               : ""
           } ${
             error
               ? twMerge(
                   `${currentSelectFieldClass?.error} ${
                     currentClass?.error ? currentClass.error : ""
                   }`,
                 )
               : ""
           } ${
             state.isDisabled
               ? twMerge(
                   `${currentSelectFieldClass?.disabled} ${
                     currentClass?.disabled ? currentClass.disabled : ""
                   }`,
                 )
               : ""
           }`,
        )
    })

    return reactSelectClassNames
  }
  let labelClass = getClass({
    error,
    classes: label?.classes,
    selectFieldClasses: selectFieldClasses.label,
  })
  let formElementClass = getReactSelectClassNames({
    error,
    classes: props.field.formElement?.classes,
    selectFieldClasses: selectFieldClasses.formElement,
  })
  let formElementGroupClass = getClass({
    error,
    classes: props.field.formElementGroup?.classes,
    selectFieldClasses: selectFieldClasses.formElementGroup,
  })
  let helpElementClass = getClass({
    error,
    classes: props.field.helpElement?.classes,
    selectFieldClasses: selectFieldClasses.helpElement,
  })

  const getTooltipAttributes = ({ id, error, tooltipPlacement }) => {
    return {
      "data-tooltip-id": id,
      "data-tooltip-content": error?.message,
      "data-tooltip-place": tooltipPlacement || "bottom-end",
    }
  }

  const getFormTooltipProps = ({ error, id, tooltipProps }) => {
    return {
      isOpen: error?.message ? true : false,
      id,
      tooltipProps,
    }
  }

  let tooltipAttributes = getTooltipAttributes({
    id: formElementAttributes?.id || formElementAttributes?.name,
    error,
    tooltipPlacement: props.field.tooltipAttributes?.placement,
  })

  let formTooltipProps = getFormTooltipProps({
    error,
    id: formElementAttributes?.id || formElementAttributes?.name,
    tooltipProps: props.tooltipProps,
  })

  const formValues = useWatch({
    control: props.form.controller.control,
  })

  const formattedFormValues = formatFormValues({ formValues })

  const conditions = props.field.formElement.conditions

  const showField = conditions
    ? checkConditions({
        formValues: formattedFormValues,
        conditions,
        resetField: props.form.resetField,
      })
      ? true
      : false
    : true

  React.useEffect(() => {
    if (!showField && error) {
      props.form.clearErrors(formElementName)
    }
  }, [showField, error])

  return (
    showField && (
      <div className={formElementGroupClass} {...tooltipAttributes}>
        <FormTooltip {...formTooltipProps} />
        {label && <LabelElement {...props} className={labelClass} />}
        <Controller
          control={props.form.controller.control}
          name={formElementAttributes?.name}
          rules={{ ...formElement?.validationRules }}
          render={({ field: { ref, ...formField } }) => {
            const options = formElement?.optionsByFieldValue
              ? formElement?.optionsByFieldValue[
                  formattedFormValues[formElement?.optionFieldName]
                ]
              : formElement?.options

            return (
              <>
                {props.field.resetButton && <ResetButton {...props} />}
                <CreateableSelect
                  isClearable
                  options={options.map(option => {
                    if (!option.label || !option.value) {
                      return {
                        label: option.label || option,
                        value: option.value,
                      }
                    }
                    return option
                  })}
                  {...formField}
                  {...formElementAttributes}
                  onChange={e => {
                    if (props.field.formElement.attributes?.onChange) {
                      props.field.formElement.attributes?.onChange()
                    }
                    formField.onChange(e)
                  }}
                  ref={ref}
                  isDisabled={props.field.disabled}
                  classNames={formElementClass}
                  placeholder={formElementAttributes?.placeholder}
                  formatCreateLabel={inputValue => `${inputValue}`}
                  value={
                    // If value is a string (previous field used was not a select field)
                    formField.value && typeof formField.value !== "object"
                      ? options?.find(option => {
                          return option.value === formField.value
                        }) || null
                      : formField.value || null
                  }
                />
                {props.field.helpElement && (
                  <div className={helpElementClass}>
                    {props.field.helpElement.text}
                  </div>
                )}
              </>
            )
          }}
        />
      </div>
    )
  )
}

/* -------------------------------------------------------------------------- */
/*                              Default Component                             */
/* -------------------------------------------------------------------------- */
export default function UnderlineField(props) {
  const formEntity = props.field.formElement || props.field.inputGroup
  const formEntityType = formEntity.type
  return formEntityType === "input" || props.field.inputGroup ? (
    <InputField {...props} />
  ) : formEntityType === "datalist" ? (
    <DataListField {...props} />
  ) : (
    <SelectField {...props} />
  )
}
